// Pure decision logic for the OAuth callback page (AuthCallbackPage).
//
// Why this exists: the callback page used to read `clerk.session` exactly once,
// 3s after mount, and treat a missing session as a hard failure — navigating the
// user to /sign-in. On a slow-but-successful Google OAuth handshake the session
// had not hydrated yet at 3s, so legitimate logins were bounced to /sign-in and
// then auto-redirected to /dashboard once the session settled (a jarring flash
// plus a false `auth_callback_failed` analytics event). This function lets the
// page poll instead: report success the instant a session appears, and only
// declare failure after a deadline generous enough to outlast a slow handshake.

/**
 * How long, after the callback page mounts, we keep waiting for Clerk to
 * establish a session before treating the OAuth round-trip as failed. Must be
 * comfortably longer than a slow (e.g. international, mobile) handshake so a
 * successful-but-slow sign-in is never bounced to /sign-in mid-flight.
 * Raised 12s → 20s on 2026-06-10: PostHog showed real users' sessions hydrating
 * at ~13–15s — past the old deadline — so legitimate sign-ins fired
 * auth_callback_failed and flashed /sign-in before recovering. This deadline
 * only applies while a handshake is genuinely in flight; bare cold-landings
 * fail fast via AUTH_CALLBACK_COLD_LANDING_DEADLINE_MS below.
 */
export const AUTH_CALLBACK_FAILURE_DEADLINE_MS = 20_000;

/**
 * Shorter failure deadline for a "cold-landing": the page reached /auth/callback
 * with nothing that can complete — no Clerk handshake params anywhere, no
 * in-flight sign-in/sign-up, and zero sessions. Real international users were
 * observed (2026-06-01/02, Peshawar + Australia) pinned to the spinner for the
 * full 12s deadline before the inevitable bounce to /sign-in. When no handshake
 * can complete, fail fast. Kept above zero so a handshake that surfaces a moment
 * after mount still wins.
 */
export const AUTH_CALLBACK_COLD_LANDING_DEADLINE_MS = 3_500;

/** How often the callback page re-checks for a hydrated Clerk session. */
export const AUTH_CALLBACK_POLL_INTERVAL_MS = 500;

export type AuthCallbackOutcome = 'completed' | 'failed' | 'pending';

export interface ResolveAuthCallbackOutcomeInput {
  /** Whether Clerk reports an active session yet. */
  sessionPresent: boolean;
  /** Milliseconds elapsed since the callback page mounted. */
  elapsedMs: number;
  /**
   * Whether any OAuth handshake can still complete: Clerk handshake params were
   * present at entry, an in-flight signIn/signUp exists, or a session is already
   * forming. Defaults to `true` so callers that cannot determine this keep the
   * full, conservative deadline. When `false`, the page is on a bare cold-landing
   * (nothing to finish) and fails fast instead of waiting the full deadline.
   */
  handshakeInFlight?: boolean;
  /** A visible verification challenge is waiting for the person, not the network. */
  verificationPending?: boolean;
}

export function resolveAuthCallbackOutcome(
  { sessionPresent, elapsedMs, handshakeInFlight = true, verificationPending = false }: ResolveAuthCallbackOutcomeInput,
): AuthCallbackOutcome {
  if (sessionPresent) return 'completed';
  if (verificationPending) return 'pending';
  const deadlineMs = handshakeInFlight
    ? AUTH_CALLBACK_FAILURE_DEADLINE_MS
    : AUTH_CALLBACK_COLD_LANDING_DEADLINE_MS;
  if (elapsedMs >= deadlineMs) return 'failed';
  return 'pending';
}

// ── Unfinished-Google bounce flag ──
//
// A Google attempt that never formed a session used to end in a spinner and
// then the bare sign-in form, with nothing saying what happened. PostHog
// 2026-10-06 (session 01a10fb9): four Google attempts failed on Clerk's bot
// check, and the visitor only signed up once the extension told them to use
// email. AuthCallbackPage sets this flag on its /sign-in bounce and SignInPage
// reads it, so the name lives in one place.

/** Query flag on the /sign-in bounce: a Google attempt did not finish. */
export const GOOGLE_UNFINISHED_PARAM = 'google_unfinished';

/** Append the flag to a /sign-in URL that may already carry ?redirect_url=. */
export function withGoogleUnfinishedFlag(signInUrl: string): string {
  return `${signInUrl}${signInUrl.includes('?') ? '&' : '?'}${GOOGLE_UNFINISHED_PARAM}=1`;
}

// ── Re-entry loop breaker ──
//
// AuthCallbackPage mounts Clerk's <AuthenticateWithRedirectCallback> with
// continueSignUpUrl="/auth/callback" — it points back at this same page. When a
// sign-up lands in `missing_requirements` the page can't satisfy (e.g. a Clerk
// instance configured to require a `username`, which Google OAuth never
// supplies), AWRC re-navigates to continueSignUpUrl and the page re-enters
// itself indefinitely. PostHog 2026-06-16: one user looped 117× in ~6 minutes.
//
// The deadline logic above bounds a SINGLE mount; it can't see across re-mounts.
// These helpers add a cross-mount circuit breaker: the page counts callback
// entries within a short window (persisted in sessionStorage by the caller) and
// aborts once the count is unreasonable, so a misconfiguration fails fast with a
// clear message instead of pinning the user in a redirect loop.

/** Max callback entries within the window before we treat it as a runaway loop. */
export const AUTH_CALLBACK_LOOP_MAX_ENTRIES = 3;

/** Window over which entries are counted. Past this, the counter resets. */
export const AUTH_CALLBACK_LOOP_WINDOW_MS = 60_000;

export interface AuthCallbackEntryState {
  /** Epoch ms of the first entry in the current window. */
  first: number;
  /** Number of entries (this one included) within the window. */
  count: number;
}

/**
 * Pure: given the prior entry state (or null) and the current time, return the
 * next state. Resets to a fresh window when the prior window has elapsed (or
 * there is no prior state); otherwise increments the count.
 */
export function nextAuthCallbackEntry(
  prev: AuthCallbackEntryState | null,
  now: number,
): AuthCallbackEntryState {
  if (prev && now - prev.first < AUTH_CALLBACK_LOOP_WINDOW_MS) {
    return { first: prev.first, count: prev.count + 1 };
  }
  return { first: now, count: 1 };
}

/**
 * Pure: has the callback re-entered itself enough times within the window to be
 * a runaway redirect loop, rather than a normal one-shot callback or a single
 * legitimate retry?
 */
export function isAuthCallbackLoop(entry: AuthCallbackEntryState): boolean {
  return entry.count > AUTH_CALLBACK_LOOP_MAX_ENTRIES;
}
